import { create } from 'zustand'
import { combine, subscribeWithSelector } from 'zustand/middleware'
import { type Dayjs, dayjs } from '../../dayjs.ts'
import { type DateRange, defaultDateRange } from './dateRanges.ts'

const defaultNow = dayjs()

export const defaultSpeedIdx = 1
export const useTimeline = create(
	subscribeWithSelector(
		combine(
			{
				date: defaultNow,
				dateRange: defaultDateRange,
				startTime: defaultDateRange.getRange(defaultNow).start,
				endTime: defaultDateRange.getRange(defaultNow).end,
				clock: defaultNow,
				nowLocked: true,
				skipUpdate: false,
				isPlaying: false,
				isDragging: false,
				repeat: true,
				animationEndedAt: undefined as Dayjs | undefined,
				speedIdx: defaultSpeedIdx,
				markerAreaEl: null as HTMLDivElement | null,
			},
			(set, get) => ({
				setDate: (date: Dayjs, skipUpdate?: boolean) => {
					set({ date: date.clamp(get().startTime, get().endTime), skipUpdate: skipUpdate ?? false })
				},
				setSelectedRange: (dateRange: DateRange) =>
					set(({ clock }) => {
						const { start: startTime, end: endTime } = dateRange.getRange(clock)
						const clockOutOfRange = clock.isBefore(startTime) || clock.isAfter(endTime)
						return {
							dateRange,
							startTime,
							endTime,
							date: clockOutOfRange ? startTime : clock,
							nowLocked: !clockOutOfRange,
							isPlaying: false,
						}
					}),
				setClock: (clock: Dayjs) =>
					set(({ dateRange }) => {
						const { start, end } = dateRange.getRange(clock)
						const clockOutOfRange = clock.isBefore(start) || clock.isAfter(end)
						return { startTime: start, endTime: end, clock, nowLocked: clockOutOfRange ? false : get().nowLocked }
					}),
				setNowLocked: (nowLocked: boolean) => set(({ clock, date }) => ({ nowLocked, date: nowLocked ? clock : date, isPlaying: false })),
				resetDateToClock: () => set({ date: get().clock, isPlaying: false }),
				continueUpdates: () => set({ skipUpdate: false }),
				togglePlay: () =>
					set(({ isPlaying, date, startTime, endTime }) => {
						const startingPlayback = !isPlaying
						const atEnd = startingPlayback && date.percentBetween(startTime, endTime) >= 1
						return { isPlaying: startingPlayback, nowLocked: false, date: atEnd ? startTime : date }
					}),
				resetToDefaultRange: () =>
					set(({ clock }) => {
						const { start, end } = defaultDateRange.getRange(clock)
						return {
							isPlaying: false,
							nowLocked: true,
							dateRange: defaultDateRange,
							startTime: start,
							endTime: end,
							date: clock,
						}
					}),
				toggleRepeat: () => set(({ repeat }) => ({ repeat: !repeat })),
				setPlaySpeed: (speedIdx: number) => set({ speedIdx }),
				playProgress: (delta: number) => {
					const { speedIdx, repeat, animationEndedAt, startTime, endTime, isDragging } = get()
					const x = get().date.percentBetween(startTime, endTime)
					let newX = x + delta * 0.001 * calculateSpeed(speedIdx)
					newX = Math.max(0, Math.min(newX, 1))

					if (newX === 1 && !isDragging) {
						if (animationEndedAt) {
							if (animationEndedAt.add(2, 'second').isAfter(dayjs())) return // Wait a bit at end before repeating the animation
							set({ animationEndedAt: undefined })
							newX = 0
						} else {
							if (repeat) set({ animationEndedAt: dayjs() })
							else set({ isPlaying: false })
						}
					}
					set({ date: startTime.add(newX * endTime.diff(startTime), 'ms') })
				},
				setDragging: (isDragging: boolean) =>
					set({ isDragging, nowLocked: isDragging ? false : get().nowLocked, animationEndedAt: undefined }),
				getMinutesSinceStart: (date: Dayjs | undefined = undefined, intervalMinutes: number = 1) => {
					const state = get()
					if (!state.startTime) return Infinity
					const time = date ?? state.date
					const adjustedStartTime = Math.floor(state.startTime.valueOf() / (intervalMinutes * 60_000)) * (intervalMinutes * 60_000)
					return Math.floor(time.diff(dayjs(adjustedStartTime), 'minute'))
				},
				setMarkerAreaEl: (el: HTMLDivElement | null) => set({ markerAreaEl: el }),
			}),
		),
	),
)

// Values are seconds it take to complete a full animation of the timeline
export const speeds = [15, 30, 60, 120] as const
export const calculateSpeed = (speedIdx: number) => 1 / (speeds[speedIdx] ?? speeds[defaultSpeedIdx])
